import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, MapPin, Clock, UserPlus, CheckCircle } from "lucide-react";
import { useLang } from "@/LanguageContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsSection() {
  const { lang } = useLang();
  const tx = t[lang];
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regDialog, setRegDialog] = useState(null);
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [regSending, setRegSending] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [regError, setRegError] = useState("");
  const [regHoneypot, setRegHoneypot] = useState("");
  const regTs = useRef(0);

  useEffect(() => {
    axios.get(`${API}/events`).then(({ data }) => setEvents(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const openRegDialog = (event) => {
    setRegDialog(event); setRegForm({ name: "", email: "", phone: "", message: "" });
    setRegHoneypot(""); regTs.current = Math.floor(Date.now() / 1000);
    setRegSuccess(false); setRegError("");
  };

  const handleRegister = async () => {
    setRegSending(true); setRegError("");
    try {
      await axios.post(`${API}/events/${regDialog.id}/register`, { ...regForm, _website: regHoneypot, _ts: regTs.current });
      setRegSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setRegError(typeof detail === "string" ? detail : (lang === "de" ? "Anmeldung fehlgeschlagen." : "Registration failed."));
    } finally { setRegSending(false); }
  };

  return (
    <section id="veranstaltungen" data-testid="events-section" className="py-16 sm:py-24 lg:py-32 bg-[#12202f] relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3A4F60]/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 sm:mb-16">
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] text-[#C5993A] mb-3 sm:mb-4">{tx.events_tag}</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl tracking-tight font-light text-white">{tx.events_h2}</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-4" data-testid="events-list">
            {events.map((event, i) => (
              <div key={event.id || i} data-testid={`event-item-${i}`} className="group bg-[#0C1620] border border-[#3A4F60]/30 rounded-lg p-5 sm:p-6 lg:p-8 hover:border-[#C5993A]/40 transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                  <div className="flex-shrink-0 w-16 sm:w-20 text-center">
                    <span className="font-heading text-2xl sm:text-3xl text-[#C5993A] font-semibold">{new Date(event.date + "T00:00:00").getDate()}</span>
                    <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#8899AA]">
                      {new Date(event.date + "T00:00:00").toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { month: "short", year: "numeric" })}
                    </p>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg sm:text-xl text-white font-medium mb-1">{event.title}</h3>
                    {event.description && <p className="text-[#8899AA] text-xs sm:text-sm font-light">{event.description}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-[#8899AA]">
                    {event.time && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C5993A]" />{event.time}</span>}
                    {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C5993A]" />{event.location}</span>}
                  </div>
                  <Button data-testid={`register-event-${i}`} onClick={() => openRegDialog(event)} className="bg-transparent border border-[#C5993A] text-[#C5993A] hover:bg-[#C5993A]/10 transition-all flex-shrink-0 text-xs sm:text-sm">
                    <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />{tx.events_register}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!regDialog} onOpenChange={(open) => { if (!open) setRegDialog(null); }}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-lg" aria-describedby="reg-dialog-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{regSuccess ? tx.events_reg_success_title : `${tx.events_reg_title}: ${regDialog?.title || ""}`}</DialogTitle>
            <p id="reg-dialog-desc" className="sr-only">Registration form</p>
          </DialogHeader>
          {regSuccess ? (
            <div className="text-center py-6" data-testid="registration-success">
              <CheckCircle className="w-14 h-14 text-[#C5993A] mx-auto mb-4" />
              <p className="text-[#F0F4F8] mb-2">{tx.events_reg_success_text}</p>
              <Button data-testid="close-registration-dialog" onClick={() => setRegDialog(null)} className="mt-4 bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">{tx.events_reg_close}</Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                  <input type="text" name="_website" value={regHoneypot} onChange={(e) => setRegHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
                </div>
                <div className="bg-[#0C1620] rounded-lg p-4 border border-[#3A4F60]/30">
                  <p className="text-white font-medium">{regDialog?.title}</p>
                  <p className="text-[#8899AA] text-sm mt-1">{regDialog?.date && formatDate(regDialog.date)}{regDialog?.time && ` · ${regDialog.time}`}{regDialog?.location && ` · ${regDialog.location}`}</p>
                </div>
                <div><label className="block text-xs text-[#8899AA] mb-1.5">{tx.events_reg_name}</label><Input data-testid="reg-name-input" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1.5">{tx.events_reg_email}</label><Input data-testid="reg-email-input" type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1.5">{tx.events_reg_phone}</label><Input data-testid="reg-phone-input" type="tel" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1.5">{tx.events_reg_message}</label><Textarea data-testid="reg-message-input" value={regForm.message} onChange={(e) => setRegForm({ ...regForm, message: e.target.value })} rows={2} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A] resize-none" /></div>
                {regError && <p data-testid="reg-error" className="text-red-400 text-xs">{regError}</p>}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRegDialog(null)} className="text-[#8899AA]">{tx.events_reg_cancel}</Button>
                <Button data-testid="submit-registration-button" onClick={handleRegister} disabled={regSending || !regForm.name || !regForm.email} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
                  {regSending ? tx.events_reg_sending : tx.events_reg_submit}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
